import { useState, useMemo } from 'react';
import { checkAgeEstimates, CheckAgeEstimatesOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2, XCircle, Download, RotateCcw, Zap, Users } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

type Result = CheckAgeEstimatesOutputType['results'][0];

const LOGO = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

interface Props {
  sectionId: string;
  sectionTitle: string;
  sectionBody: string;
}

// Age ranges used for both UI selection AND AI validation
export const AGE_RANGES = [
  { value: 'under-18', label: 'Under 18  (14–17)' },
  { value: '18-20',    label: '18–20 years' },
  { value: '21-24',    label: '21–24 years' },
  { value: '25-29',    label: '25–29 years' },
  { value: '30-34',    label: '30–34 years' },
  { value: '35-39',    label: '35–39 years' },
  { value: '40-44',    label: '40–44 years' },
  { value: '45-49',    label: '45–49 years' },
  { value: '50-54',    label: '50–54 years' },
  { value: '55-59',    label: '55–59 years' },
  { value: '60+',      label: '60+ years' },
];

function extractImages(body: string): Array<{ url: string; label: string }> {
  const regex = /!\[.*?\]\((https?:\/\/[^)]+)\)/g;
  const allUrls: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(body)) !== null) {
    allUrls.push(match[1]);
  }
  // Skip the 8th image (index 7) — AI cannot reliably estimate that person,
  // so person 9 renumbers to person 8 giving 8 people total.
  const filtered = allUrls.filter((_, i) => i !== 7);
  return filtered.map((url, i) => ({ url, label: `Person ${i + 1}` }));
}

export default function AgeEstimationQuiz({ sectionId, sectionTitle, sectionBody }: Props) {
  const images = useMemo(() => extractImages(sectionBody), [sectionBody]);
  // estimates store the selected range value string (e.g. "18-20")
  const [estimates, setEstimates] = useState<Record<string, string>>({});
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState<CheckAgeEstimatesOutputType | null>(null);

  if (images.length === 0) return null;

  const answeredCount = Object.keys(estimates).length;
  const allAnswered = images.every(img => estimates[img.url] !== undefined);

  const handleCheck = async () => {
    if (!allAnswered) {
      toast.error(`Please estimate all ${images.length} ages first`);
      return;
    }
    setChecking(true);
    try {
      const userEstimates = images.map(img => ({
        url: img.url,
        label: img.label,
        ageRange: estimates[img.url] ?? 'unknown',
      }));
      const res = await checkAgeEstimates({ sectionId, sectionTitle, userEstimates });
      setResults(res);
    } catch (e: any) {
      toast.error(e.message || 'Failed to check estimates');
    } finally {
      setChecking(false);
    }
  };

  const retake = () => {
    setResults(null);
    setEstimates({});
  };

  const download = () => {
    if (!results) return;
    const year = new Date().getFullYear();
    const gridItems = results.results.map(r => `
      <div class="img-card ${r.passed ? 'pass' : 'fail'}">
        <img src="${r.url}" alt="${r.label}" class="photo" />
        <div class="card-body">
          <p class="person">${r.label}</p>
          <div class="ages">
            <div class="age-row"><span class="label">Your range</span><span class="val">${r.userRange}</span></div>
            <div class="age-row"><span class="label">AI estimate</span><span class="val">${r.aiAge} yrs (${r.ageRange})</span></div>
          </div>
          <div class="badge ${r.passed ? 'pass-badge' : 'fail-badge'}">${r.passed ? '✓ CORRECT RANGE' : '✗ REVIEW'}</div>
          <p class="feedback">${r.feedback}</p>
        </div>
      </div>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Age Estimation — ${sectionTitle}</title>
<style>body{font-family:system-ui,sans-serif;color:#1a1a1a;max-width:900px;margin:0 auto;padding:40px}
.header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #A36B58;padding-bottom:16px;margin-bottom:24px}
.logo{height:48px}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px}
.img-card{border:2px solid;border-radius:8px;overflow:hidden}
.img-card.pass{border-color:#86efac}.img-card.fail{border-color:#fca5a5}
.photo{width:100%;height:150px;object-fit:cover;display:block}
.card-body{padding:10px}
.person{font-weight:600;font-size:14px;margin:0 0 6px}
.ages{font-size:12px;margin-bottom:8px}
.age-row{display:flex;justify-content:space-between;padding:2px 0}
.label{color:#6b7280}.val{font-weight:600}
.badge{display:inline-block;font-size:11px;font-weight:700;padding:3px 8px;border-radius:4px;margin-bottom:6px}
.pass-badge{background:#d1fae5;color:#065f46}.fail-badge{background:#fee2e2;color:#991b1b}
.feedback{font-size:12px;color:#374151;font-style:italic;margin:0}
.stats{display:flex;gap:12px;margin-bottom:20px}
.stat{background:#f3f4f6;border-radius:6px;padding:8px 14px;font-size:14px}
.overall{border:2px solid;border-radius:8px;padding:14px;margin-bottom:20px}
.overall.pass{background:#d1fae5;border-color:#86efac}.overall.fail{background:#fee2e2;border-color:#fca5a5}
@media print{@page{margin:15mm 15mm}.grid{grid-template-columns:repeat(4,1fr)}}
</style></head><body>
<div class="header"><img class="logo" src="${LOGO}" alt="Rattlebone" /><span style="font-size:12px;color:#888;text-transform:uppercase">Age Estimation Exercise</span></div>
<h1 style="font-size:20px;font-weight:700;margin:0 0 16px">${sectionTitle}</h1>
<div class="stats">
  <div class="stat">Score: <strong>${results.score}/${results.totalImages}</strong></div>
  <div class="stat">Accuracy: <strong>${results.percentage}%</strong></div>
  <div class="stat">Points: <strong>+${results.pointsEarned}</strong></div>
  <div class="stat">Date: <strong>${new Date().toLocaleDateString('en-GB')}</strong></div>
</div>
<div class="overall ${results.percentage >= 60 ? 'pass' : 'fail'}">
  <strong>${results.percentage >= 60 ? '✓ Passed' : '✗ Re-take required'}</strong> — ${results.overallFeedback}
</div>
<div class="grid">${gridItems}</div>
<p style="font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px">© Rattlebone Sherston Ltd ${year}. Confidential — staff training record.</p>
</body></html>`;

    const w = window.open('', '_blank', 'width=1000,height=800');
    if (!w) { toast.error('Please allow pop-ups'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 800);
  };

  return (
    <div className="mt-6">
      {/* Header */}
      <div className="border border-primary/20 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 p-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Age Estimation Exercise</h3>
            <p className="text-sm text-muted-foreground">
              Look at each photo and select the age range you think the person falls into. Then click <strong>CHECK</strong> — AI will assess whether your ranges are correct.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {answeredCount}/{images.length} estimated · Earn up to <strong>{5 + images.length * 10 + 15} points</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Image grid — 4 columns to fit 8 people (2 rows × 4) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {images.map((img) => {
          const result = results?.results.find(r => r.url === img.url);
          const selectedLabel = AGE_RANGES.find(r => r.value === estimates[img.url])?.label;
          return (
            <div
              key={img.url}
              className={cn(
                'border rounded-xl overflow-hidden bg-card transition-all',
                result?.passed === true  ? 'border-green-300' :
                result?.passed === false ? 'border-red-300'   : 'border-border'
              )}
            >
              {/* Photo */}
              <div className="relative">
                <img
                  src={img.url}
                  alt={img.label}
                  className="w-full object-cover"
                  style={{ height: '140px' }}
                />
                {result && (
                  <div className={cn(
                    'absolute top-1.5 right-1.5 rounded-full w-6 h-6 flex items-center justify-center',
                    result.passed ? 'bg-green-500' : 'bg-red-500'
                  )}>
                    {result.passed
                      ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <XCircle className="w-4 h-4 text-white" />}
                  </div>
                )}
              </div>

              {/* Card body */}
              <div className="p-2">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">{img.label}</p>

                {!results ? (
                  <Select
                    value={estimates[img.url] || ''}
                    onValueChange={v => setEstimates(prev => ({ ...prev, [img.url]: v }))}
                    disabled={checking}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select range…" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_RANGES.map(r => (
                        <SelectItem key={r.value} value={r.value} className="text-xs">
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between gap-1">
                      <span className="text-muted-foreground">Your range:</span>
                      <span className="font-medium text-right">{selectedLabel}</span>
                    </div>
                    <div className="flex justify-between gap-1">
                      <span className="text-muted-foreground">AI range:</span>
                      <span className="font-semibold text-primary text-right">{result?.ageRange}</span>
                    </div>
                    {result?.feedback && (
                      <p className="text-xs text-muted-foreground italic border-t border-border pt-1 mt-1 leading-tight">
                        {result.feedback}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Results summary */}
      {results && (
        <div className={cn(
          'rounded-xl p-4 border mb-4',
          results.percentage >= 60 ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'
        )}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-bold text-foreground">
                {results.percentage >= 60 ? '✅ Passed!' : '❌ Re-take Required'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{results.overallFeedback}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold text-foreground">{results.score}/{results.totalImages}</p>
              <p className="text-xs text-muted-foreground">correct</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-sm">
            <Zap className="w-4 h-4 text-primary" />
            <span className="font-semibold text-primary">+{results.pointsEarned} points earned!</span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!results ? (
        <Button
          className="w-full text-base py-3 h-auto"
          onClick={handleCheck}
          disabled={checking || !allAnswered}
        >
          {checking
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI is checking your estimates…</>
            : !allAnswered
              ? `Select a range for all photos (${images.length - answeredCount} remaining)`
              : `🔍 CHECK — Verify My Age Ranges`}
        </Button>
      ) : (
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1 gap-2" onClick={download}>
            <Download className="w-4 h-4" /> Download Results
          </Button>
          <Button variant="outline" className="flex-1 gap-2" onClick={retake}>
            <RotateCcw className="w-4 h-4" /> Try Again
          </Button>
        </div>
      )}
    </div>
  );
}
